import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Alert, Badge, Spinner } from 'flowbite-react';
import classNames from 'classnames';
import {
  HiArrowLeft,
  HiOutlineExclamation,
  HiOutlineExternalLink,
} from 'react-icons/hi';
import Title from '../../components/Title';
import Button from '../../components/Button';
import { fetchMldDeal } from '../../api/mld/actions';
import { mldQueryKeys } from '../../api/mld/queryKeys';
import {
  MLD_NOT_FOUND,
  MldDealView,
  MldDetailResponse,
  MldOtherDeal,
} from '../../api/mld/types';
import {
  formatCents,
  formatMldDateTime,
  formatMldTimestamp,
  mldCancellationScheduledLine,
  mldChargebeeCustomerUrl,
  mldChargebeeInvoiceUrl,
  mldChargebeeSubscriptionUrl,
  MldDisplayLedgerEntry,
  mldDisplayLedger,
  mldFamilyLabel,
  mldHistoryWhat,
  mldImportFootnote,
  mldPaymentAmount,
  mldPaymentInvoiceIds,
  mldPaymentItem,
  MldPlanNameLookup,
  mldPlanNameLookup,
  mldSaleTypeLabel,
  mldSplitImported,
  mldSplitLedger,
  mldStatusLabel,
  mldSubscriptionTypeLabel,
  mldVisibleDates,
  mldWarningNotes,
  mldWho,
} from './helpers';
import { MldPreviewLabel, MldStatusChip } from './MldStatusChip';
import { MLD_LIST_PATH, MldBack, readMldBack } from './mldListQuery';
import { formatMessage, mldStrings } from './strings';

const S = mldStrings;
const D = mldStrings.detail;

const card = 'rounded-lg border border-neutral-100 bg-white p-5 shadow-sm';
const cardHeading = 'mb-3 text-sm font-semibold text-neutral-700';
const fieldLabel = 'text-xs uppercase text-neutral-500';
// Brand cyan text is below 4.5:1 on white (PRD accessibility), so links keep
// dark text and carry the brand colour in the underline.
const linkClass =
  'text-sm font-medium text-neutral-800 underline decoration-primary-500 underline-offset-2 hover:text-primary-700';

/** True only for the detail route's own 404 (`{ code: 'MLD_NOT_FOUND' }`). */
const isDealNotFound = (error: unknown): boolean => {
  const response = (
    error as { response?: { status?: number; data?: { code?: unknown } } }
  )?.response;
  return response?.status === 404 && response.data?.code === MLD_NOT_FOUND;
};

/**
 * The Back link's label and router state (ADR-004): the member target asks
 * the member page to open on its MLD tab.
 */
const backLinkProps = (back: MldBack) =>
  back.kind === 'member'
    ? { to: back.to, state: { memberTab: 'mld' }, label: D.backToMember }
    : { to: back.to, state: undefined, label: D.backToList };

const memberName = (deal: MldDealView): string => {
  const name = [deal.member.firstName, deal.member.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return name || deal.member.email || D.unknownMember;
};

/**
 * The Native Member List Deal detail, `/mld/:subscriptionId` (PRD Core
 * Feature 3, mock Option A). Status first, then plan and the dates that apply,
 * then the payments with their sale type and who took them; on the right, the
 * links out, the member's other deals and the status history in true order.
 *
 * Read-only (ADR-007): no mutation, form or action button. The only button is
 * "Try again" on a load error. Layout follows `printShop/RmQueue/JobDetail`.
 *
 * Back returns to where the operator came from (router state `mldBack`,
 * ADR-004): the list as it was, or the member's MLD tab; `/mld` otherwise.
 */
export default function MldDetail() {
  const { subscriptionId = '' } = useParams<{ subscriptionId: string }>();
  const location = useLocation();
  const back = backLinkProps(readMldBack(location.state));

  const query = useQuery(
    mldQueryKeys.detail(subscriptionId),
    () => fetchMldDeal(subscriptionId),
    {
      enabled: Boolean(subscriptionId),
      // One read per open; a failure shows at once and the operator retries.
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  return (
    <div className="pt-8 min-h-screen w-full" data-testid="mld-detail">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Link
          to={back.to}
          state={back.state}
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-800"
          data-testid="mld-detail-back"
        >
          <HiArrowLeft className="h-4 w-4" aria-hidden /> {back.label}
        </Link>
        <MldPreviewLabel />
      </div>

      {query.isLoading && (
        <div className="flex justify-center pt-16">
          <Spinner data-testid="mld-detail-loading" />
        </div>
      )}

      {query.isError &&
        (isDealNotFound(query.error) ? (
          <div
            className="pt-16 text-center text-neutral-600"
            data-testid="mld-detail-not-found"
          >
            <p className="mb-3">{D.notFound}</p>
            <Link to={back.to} state={back.state} className={linkClass}>
              {back.label}
            </Link>
          </div>
        ) : (
          <Alert
            color="failure"
            icon={HiOutlineExclamation}
            data-testid="mld-detail-error"
          >
            <div className="flex flex-wrap items-center gap-4">
              <span>{D.loadError}</span>
              <Button
                variant="LIGHT"
                size="xs"
                isProcessing={query.isFetching}
                disabled={query.isFetching}
                onClick={() => void query.refetch()}
                data-testid="mld-detail-retry"
              >
                {D.retry}
              </Button>
            </div>
          </Alert>
        ))}

      {query.data && (
        <DealDetail data={query.data} routerState={location.state} />
      )}
    </div>
  );
}

function DealDetail({
  data,
  routerState,
}: {
  data: MldDetailResponse;
  /** The incoming router state, forwarded on the other-deal links. */
  routerState: unknown;
}) {
  const { deal, otherDeals, chargebeeAppUrl } = data;
  // An imported deal shows its snapshot payment as a display-only row
  // (ADR-005), so the detail tells the same story as the list. The import
  // itself is where the history starts, not an event: it leaves the dated
  // rows and becomes one footnote under the history (ADR-007 decision 7).
  const { entries, imported } = mldSplitImported(mldDisplayLedger(deal));
  const { payments, history } = mldSplitLedger(entries);
  // Plan names from this deal and the member's other deals (ADR-007
  // decision 2). Other deals carry no ledger, only their current plan.
  const planNames = mldPlanNameLookup([
    deal,
    ...otherDeals.map((other) => ({ plan: other.plan, ledger: [] })),
  ]);

  return (
    <>
      <div className="mb-6">
        <Title>
          {formatMessage(D.title, {
            member: memberName(deal),
            plan: deal.plan.name ?? S.unknown,
          })}
        </Title>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <StatusCard deal={deal} />
          <PlanCard deal={deal} />
          <PaymentsCard payments={payments} planNames={planNames} />
        </div>

        <div className="space-y-6">
          <LinksCard
            deal={deal}
            chargebeeAppUrl={chargebeeAppUrl}
            invoiceIds={mldPaymentInvoiceIds(payments)}
          />
          <OtherDealsCard otherDeals={otherDeals} routerState={routerState} />
          <HistoryCard
            history={history}
            planNames={planNames}
            footnote={mldImportFootnote(imported)}
          />
        </div>
      </div>
    </>
  );
}

function StatusCard({ deal }: { deal: MldDealView }) {
  const cancellation = mldCancellationScheduledLine(deal);
  const notes = mldWarningNotes(deal.warnings);
  return (
    <section className={card} data-testid="mld-detail-status">
      <h3 className={cardHeading}>{D.status.heading}</h3>
      <MldStatusChip deal={deal} />
      {cancellation && (
        <p
          className="mt-3 text-sm text-neutral-700"
          data-testid="mld-detail-cancellation"
        >
          {cancellation}
        </p>
      )}
      {notes.length > 0 && (
        <div className="mt-4" data-testid="mld-detail-warnings">
          <p className={fieldLabel}>{D.warnings.heading}</p>
          <ul className="mt-1 list-disc pl-5 text-sm text-neutral-500">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function PlanCard({ deal }: { deal: MldDealView }) {
  const rows: Array<{ key: string; label: string; value: string }> = [
    { key: 'plan', label: D.plan.plan, value: deal.plan.name ?? S.unknown },
    {
      key: 'price',
      label: D.plan.price,
      value: formatCents(deal.plan.amountCents, deal.plan.currency),
    },
    {
      key: 'subscriptionType',
      label: D.plan.subscriptionType,
      value: mldSubscriptionTypeLabel(deal.plan.subscriptionType),
    },
    { key: 'family', label: D.plan.family, value: mldFamilyLabel(deal.family) },
    ...mldVisibleDates(deal).map((field) => ({
      key: field.key,
      label: field.label,
      value: field.value,
    })),
  ];
  return (
    <section className={card} data-testid="mld-detail-plan">
      <h3 className={cardHeading}>{D.plan.heading}</h3>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.key} data-testid={`mld-detail-field-${row.key}`}>
            <dt className={fieldLabel}>{row.label}</dt>
            <dd className="text-sm text-neutral-800">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * A row's date and time, "Oct 8 · 1:30 PM", with the full instant on hover
 * (ADR-007 decision 4).
 */
function RowTime({ at, testId }: { at: string; testId: string }) {
  return (
    <time dateTime={at} title={formatMldTimestamp(at)} data-testid={testId}>
      {formatMldDateTime(at)}
    </time>
  );
}

function PaymentRow({
  entry,
  index,
  planNames,
}: {
  entry: MldDisplayLedgerEntry;
  index: number;
  planNames: MldPlanNameLookup;
}) {
  const failed = entry.kind === 'PAYMENT_FAILED';
  return (
    <tr
      className="border-t border-neutral-100 align-top"
      data-testid={`mld-detail-payment-${index}`}
    >
      <td className="py-2 pr-4 whitespace-nowrap">
        <RowTime at={entry.at} testId={`mld-detail-payment-time-${index}`} />
      </td>
      <td className="py-2 pr-4">
        <span className="inline-flex flex-wrap items-center gap-1">
          <Badge
            color={failed ? 'failure' : 'success'}
            size="sm"
            className="w-fit"
          >
            {failed ? D.payments.failed : D.payments.paid}
          </Badge>
          {entry.importedPayment && (
            <Badge
              color="gray"
              size="sm"
              className="w-fit"
              data-testid={`mld-detail-payment-imported-${index}`}
            >
              {D.payments.imported}
            </Badge>
          )}
        </span>
        {failed && entry.failureReason && (
          <p className="mt-1 text-xs text-neutral-600">{entry.failureReason}</p>
        )}
      </td>
      <td className="py-2 pr-4" data-testid={`mld-detail-payment-item-${index}`}>
        {/* The plan it paid for, or what a one-time charge bought. */}
        {mldPaymentItem(entry, planNames) ?? S.none}
      </td>
      <td className="py-2 pr-4 whitespace-nowrap">
        {failed
          ? formatMessage(D.payments.amountDue, {
              amount: formatCents(entry.amountCents, entry.currency),
            })
          : mldPaymentAmount(entry)}
      </td>
      <td className="py-2 pr-4">
        <span className="inline-flex flex-wrap items-center gap-1">
          {/* `null` is a real answer here: no sale type. */}
          {entry.saleType ? mldSaleTypeLabel(entry.saleType) : S.none}
          {entry.trialPayment && (
            <Badge color="indigo" size="sm" className="w-fit">
              {S.chip.trialSale}
            </Badge>
          )}
        </span>
      </td>
      <td className="py-2">
        {/* No who for an imported or unknown-origin payment (as the list). */}
        {mldWho(entry.actor, entry.operatorEmail) ?? S.none}
      </td>
    </tr>
  );
}

function PaymentsCard({
  payments,
  planNames,
}: {
  payments: MldDisplayLedgerEntry[];
  planNames: MldPlanNameLookup;
}) {
  const columns = D.payments.columns;
  return (
    <section className={card} data-testid="mld-detail-payments">
      <h3 className={cardHeading}>{D.payments.heading}</h3>
      {payments.length === 0 ? (
        <p className="text-sm text-neutral-500">{D.payments.empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-neutral-800">
            <thead>
              <tr className={fieldLabel}>
                <th scope="col" className="pb-2 pr-4 font-medium">
                  {columns.date}
                </th>
                <th scope="col" className="pb-2 pr-4 font-medium">
                  {columns.result}
                </th>
                <th scope="col" className="pb-2 pr-4 font-medium">
                  {columns.item}
                </th>
                <th scope="col" className="pb-2 pr-4 font-medium">
                  {columns.amount}
                </th>
                <th scope="col" className="pb-2 pr-4 font-medium">
                  {columns.saleType}
                </th>
                <th scope="col" className="pb-2 font-medium">
                  {columns.who}
                </th>
              </tr>
            </thead>
            <tbody>
              {payments.map((entry, index) => (
                <PaymentRow
                  key={`${entry.eventId}-${index}`}
                  entry={entry}
                  index={index}
                  planNames={planNames}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Opens Chargebee in a new tab, never with access to this window. */
function ExternalLink({
  href,
  label,
  testId,
}: {
  href: string;
  label: string;
  testId: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={classNames(linkClass, 'inline-flex items-center gap-1')}
      data-testid={testId}
    >
      {label}
      <HiOutlineExternalLink className="h-4 w-4" aria-hidden />
    </a>
  );
}

function LinksCard({
  deal,
  chargebeeAppUrl,
  invoiceIds,
}: {
  deal: MldDealView;
  chargebeeAppUrl: string;
  invoiceIds: string[];
}) {
  const subscriptionUrl = mldChargebeeSubscriptionUrl(
    chargebeeAppUrl,
    deal.subscriptionId,
  );
  const customerUrl = mldChargebeeCustomerUrl(
    chargebeeAppUrl,
    deal.chargebeeCustomerId,
  );
  return (
    <section className={card} data-testid="mld-detail-links">
      <h3 className={cardHeading}>{D.links.heading}</h3>
      <ul className="space-y-2">
        {deal.customerId && (
          <li>
            <Link
              to={`/customer-information/${encodeURIComponent(deal.customerId)}`}
              className={linkClass}
              data-testid="mld-detail-member-link"
            >
              {D.links.member}
            </Link>
          </li>
        )}
        {subscriptionUrl && (
          <li>
            <ExternalLink
              href={subscriptionUrl}
              label={D.links.subscription}
              testId="mld-detail-chargebee-subscription"
            />
          </li>
        )}
        {customerUrl && (
          <li>
            <ExternalLink
              href={customerUrl}
              label={D.links.customer}
              testId="mld-detail-chargebee-customer"
            />
          </li>
        )}
        {invoiceIds.map((invoiceId) => {
          const url = mldChargebeeInvoiceUrl(chargebeeAppUrl, invoiceId);
          return url ? (
            <li key={invoiceId}>
              <ExternalLink
                href={url}
                label={formatMessage(D.links.invoice, { invoiceId })}
                testId={`mld-detail-chargebee-invoice-${invoiceId}`}
              />
            </li>
          ) : null;
        })}
      </ul>
    </section>
  );
}

function OtherDealsCard({
  otherDeals,
  routerState,
}: {
  otherDeals: MldOtherDeal[];
  routerState: unknown;
}) {
  if (otherDeals.length === 0) {
    return null;
  }
  return (
    <section className={card} data-testid="mld-detail-other-deals">
      <h3 className={cardHeading}>{D.otherDeals.heading}</h3>
      <ul className="space-y-2">
        {otherDeals.map((other) => (
          <li
            key={other.subscriptionId}
            className="flex flex-wrap items-center gap-2"
          >
            <Link
              to={`${MLD_LIST_PATH}/${encodeURIComponent(other.subscriptionId)}`}
              // Keeps the Back target after hopping to another deal.
              state={routerState}
              className={linkClass}
              data-testid={`mld-detail-other-deal-${other.subscriptionId}`}
            >
              {formatMessage(D.otherDeals.item, {
                family: mldFamilyLabel(other.family),
                plan: other.plan.name ?? S.unknown,
                subscriptionId: other.subscriptionId,
              })}
            </Link>
            <span className="text-xs text-neutral-600">
              {mldStatusLabel(other.status)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * One history line: what changed, with plans named through the lookup
 * (ADR-007 decision 2), then "date and time · who", or the date and time
 * alone when nobody can be named (`SYSTEM`). The import is not a line: it is
 * the card's footnote.
 */
function HistoryEntry({
  entry,
  index,
  planNames,
}: {
  entry: MldDisplayLedgerEntry;
  index: number;
  planNames: MldPlanNameLookup;
}) {
  const who = mldWho(entry.actor, entry.operatorEmail);
  return (
    <>
      <p className="text-sm text-neutral-800">
        {mldHistoryWhat(entry, planNames)}
      </p>
      <p className="text-xs text-neutral-600">
        <RowTime at={entry.at} testId={`mld-detail-history-time-${index}`} />
        {who && formatMessage(D.history.whoAfterTime, { who })}
      </p>
    </>
  );
}

function HistoryCard({
  history,
  planNames,
  footnote,
}: {
  history: MldDisplayLedgerEntry[];
  planNames: MldPlanNameLookup;
  /** "Imported from Chargebee on …", or `null` for a deal not imported. */
  footnote: string | null;
}) {
  return (
    <section className={card} data-testid="mld-detail-history">
      <h3 className={cardHeading}>{D.history.heading}</h3>
      {history.length === 0 ? (
        <p className="text-sm text-neutral-500">{D.history.empty}</p>
      ) : (
        <ol className="space-y-3">
          {history.map((entry, index) => (
            <li
              key={`${entry.eventId}-${index}`}
              className="border-l-2 border-neutral-100 pl-3"
              data-testid={`mld-detail-history-${index}`}
            >
              <HistoryEntry entry={entry} index={index} planNames={planNames} />
            </li>
          ))}
        </ol>
      )}
      {footnote && (
        <p
          className="mt-3 text-xs text-neutral-600"
          data-testid="mld-detail-import-footnote"
        >
          {footnote}
        </p>
      )}
    </section>
  );
}
